import type { Node as ProseMirrorNode } from "prosemirror-model";
import { Slice } from "prosemirror-model";
import type { EditorState } from "prosemirror-state";
import { Plugin } from "prosemirror-state";

import { EDITOR_MODE } from "../constants";
import { parseHtmlToDoc } from "../markdown/html";
import {
  parseMarkdownToDoc,
  serializeDocToMarkdown,
} from "../markdown/markdown";
import { parseMarkdownSourceToDoc } from "../markdown/source-doc";
import {
  getImageFilesFromClipboardData,
  insertImageFilesIntoEditor,
} from "./image-files";

const isSelectionInCodeBlock = (state: EditorState) => {
  const { $from } = state.selection;

  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.spec.code) {
      return true;
    }
  }

  return false;
};

const docToSlice = (doc: ProseMirrorNode) => Slice.maxOpen(doc.content);

export const editorClipboardPlugin = ({
  mode,
  uploadImageFile,
}: {
  mode: EDITOR_MODE;
  uploadImageFile?: (file: File) => Promise<string | null>;
}) =>
  new Plugin({
    props: {
      handlePaste(view, event) {
        const clipboardData = event.clipboardData;

        if (!clipboardData) {
          return false;
        }

        const plainText = clipboardData.getData("text/plain");

        if (isSelectionInCodeBlock(view.state)) {
          if (!plainText) {
            return false;
          }

          view.dispatch(view.state.tr.insertText(plainText));
          return true;
        }

        const imageFiles = getImageFilesFromClipboardData(clipboardData);

        if (imageFiles.length) {
          event.preventDefault();
          return insertImageFilesIntoEditor({
            files: imageFiles,
            insertAt: view.state.selection.from,
            mode,
            uploadImageFile,
            view,
          });
        }

        const html = clipboardData.getData("text/html");
        const markdown =
          clipboardData.getData("text/markdown") ||
          clipboardData.getData("text/x-markdown");

        if (html) {
          const parsedDoc = parseHtmlToDoc(html);

          if (mode === EDITOR_MODE.SIMPLE) {
            view.dispatch(
              view.state.tr
                .replaceSelection(
                  docToSlice(
                    parseMarkdownSourceToDoc(serializeDocToMarkdown(parsedDoc)),
                  ),
                )
                .scrollIntoView(),
            );
            return true;
          }

          view.dispatch(
            view.state.tr
              .replaceSelection(docToSlice(parsedDoc))
              .scrollIntoView(),
          );
          return true;
        }

        if (markdown || plainText) {
          const content = markdown || plainText;

          view.dispatch(
            view.state.tr
              .replaceSelection(
                docToSlice(
                  mode === EDITOR_MODE.SIMPLE
                    ? parseMarkdownSourceToDoc(content)
                    : parseMarkdownToDoc(content),
                ),
              )
              .scrollIntoView(),
          );
          return true;
        }

        return false;
      },
    },
  });
